import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    PaymentLineItemInput,
    emptyLineItem,
    formatMoney,
    previewTotal,
} from "../../domain/types";

interface Props {
    items: PaymentLineItemInput[];
    onChange: (items: PaymentLineItemInput[]) => void;
    disabled?: boolean;
}

const inputClass =
    "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 disabled:opacity-60";

const PaymentLineItemsEditor: React.FC<Props> = ({ items, onChange, disabled }) => {
    const { t } = useTranslation();
    const total = previewTotal(items);

    const patch = (index: number, field: keyof PaymentLineItemInput, value: string) => {
        onChange(
            items.map((item, i) => (i === index ? { ...item, [field]: value } : item))
        );
    };

    const remove = (index: number) => {
        onChange(items.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-4">
            {/* A table on desktop, stacked cards on small screens: numeric
                columns do not survive a phone viewport. */}
            <div className="space-y-3">
                {items.map((item, index) => (
                    <div
                        key={index}
                        className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 p-3 sm:grid-cols-12 sm:items-end dark:border-gray-700"
                    >
                        <div className="sm:col-span-7">
                            <label
                                className="mb-1 block text-xs text-content-tertiary"
                                htmlFor={`line-description-${index}`}
                            >
                                {t("payments.fields.description")}
                            </label>
                            <input
                                id={`line-description-${index}`}
                                className={inputClass}
                                value={item.description}
                                disabled={disabled}
                                onChange={(e) => patch(index, "description", e.target.value)}
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label
                                className="mb-1 block text-xs text-content-tertiary"
                                htmlFor={`line-quantity-${index}`}
                            >
                                {t("payments.fields.quantity")}
                            </label>
                            <input
                                id={`line-quantity-${index}`}
                                className={inputClass}
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity}
                                disabled={disabled}
                                onChange={(e) => patch(index, "quantity", e.target.value)}
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label
                                className="mb-1 block text-xs text-content-tertiary"
                                htmlFor={`line-price-${index}`}
                            >
                                {t("payments.fields.unitPrice")}
                            </label>
                            <input
                                id={`line-price-${index}`}
                                className={inputClass}
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_price}
                                disabled={disabled}
                                onChange={(e) => patch(index, "unit_price", e.target.value)}
                            />
                        </div>
                        <div className="sm:col-span-1">
                            <button
                                type="button"
                                // The last line cannot go: the server refuses
                                // a payment with no lines.
                                disabled={disabled || items.length <= 1}
                                onClick={() => remove(index)}
                                aria-label={t("payments.actions.removeLine")}
                                className="flex h-10 w-full items-center justify-center rounded-lg text-content-tertiary hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-red-500/10"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {!disabled && (
                <button
                    type="button"
                    onClick={() => onChange([...items, emptyLineItem()])}
                    className="inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700"
                >
                    <Plus size={16} />
                    {t("payments.actions.addLine")}
                </button>
            )}

            {/* One total, no tax breakdown. A VAT line would make this a
                fiscal document — see domain/types.ts. */}
            <dl className="ml-auto w-full space-y-1 text-sm sm:w-64">
                <div className="flex justify-between border-t border-gray-200 pt-1 text-base font-semibold text-gray-900 dark:border-gray-700 dark:text-white">
                    <dt>{t("payments.fields.total")}</dt>
                    <dd>{formatMoney(total)}</dd>
                </div>
            </dl>
        </div>
    );
};

export default PaymentLineItemsEditor;
